import React, { createContext, useContext, useState, useCallback } from 'react';
import supabase from '../../../config/supabaseClient';
import toast from 'react-hot-toast';
import { createUserSeparateInstance } from '../utils/createUserSeparateInstance';

const GestaoUsuariosContext = createContext();

export const useGestaoUsuarios = () => {
  const context = useContext(GestaoUsuariosContext);
  if (!context) {
    throw new Error('useGestaoUsuarios deve ser usado dentro de GestaoUsuariosProvider');
  }
  return context;
};

export default function GestaoUsuariosProvider({ children }) {
  const [usuarios, setUsuarios] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    status: '',
    nivel_acesso: '',
    busca: ''
  });
  const [filtrosColaboradores, setFiltrosColaboradores] = useState({
    status: '',
    setor: '',
    busca: ''
  });

  // Carregar usuários do sistema (sem setor)
  const carregarUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('funcionariousuario')
        .select('*')
        .is('setor', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setUsuarios(data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar colaboradores (com setor)
  const carregarColaboradores = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('funcionariousuario')
        .select('*')
        .not('setor', 'is', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setColaboradores(data || []);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
      toast.error('Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar setores
  const carregarSetores = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('funcionariosetor')
        .select('*')
        .order('descricao', { ascending: true });

      if (error) throw error;
      
      setSetores(data || []);
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
      toast.error('Erro ao carregar setores');
    }
  }, []);

  // Criar usuário usando a API de signup do Supabase
  const criarUsuario = async (dadosUsuario) => {
    try {
      setLoading(true);
      console.log('Iniciando criação de usuário:', dadosUsuario.email);

      // Verificar permissões no frontend antes de tentar criar
      const { data: currentUser, error: permError } = await supabase
        .from('funcionariousuario')
        .select('nivel_acesso, permissoes, ativo')
        .eq('uid', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (permError || !currentUser) {
        throw new Error('Não foi possível verificar suas permissões');
      }

      if (currentUser.nivel_acesso !== 'admin' || 
          !currentUser.ativo || 
          currentUser.permissoes?.['gestao-usuarios'] !== true) {
        throw new Error('Você não tem permissão para criar usuários');
      }

      console.log('Permissões verificadas - prosseguindo com criação');

      // 1. Tentar criar usuário com instância isolada (não afeta sessão atual)
      let authData;
      let creationMethod = 'unknown';

      try {
        const result = await createUserSeparateInstance({
          email: dadosUsuario.email,
          password: dadosUsuario.senha_temporaria
        });

        if (result.success) {
          authData = { user: result.user };
          creationMethod = result.method;
          console.log('Usuário criado sem afetar sessão atual:', result.user.id);
        } else {
          throw new Error('Falha na criação com instância isolada');
        }
      } catch (isolatedError) {
        console.log('Instância isolada falhou, usando Admin API:', isolatedError.message);
        
        try {
          // 2. Fallback: tentar Admin API
          const { data: adminData, error: adminError } = await supabase.auth.admin.createUser({
            email: dadosUsuario.email,
            password: dadosUsuario.senha_temporaria,
            email_confirm: true,
            user_metadata: {
              created_by_admin: true
            }
          });

          if (!adminError && adminData.user) {
            authData = adminData;
            creationMethod = 'admin_api';
            console.log('Usuário criado via Admin API:', adminData.user.id);
          } else {
            throw adminError || new Error('Admin API falhou');
          }
        } catch (adminError) {
          console.log('Admin API falhou, usando signUp com restauração:', adminError.message);
          
          // 3. Último fallback: signUp normal com restauração rápida
          const { data: currentSession } = await supabase.auth.getSession();
          
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: dadosUsuario.email,
            password: dadosUsuario.senha_temporaria,
            options: {
              emailRedirectTo: undefined
            }
          });

          if (signUpError) {
            throw signUpError;
          }

          authData = signUpData;
          creationMethod = 'signup_with_restore';
          console.log('Usuário criado via signUp:', signUpData.user.id);

          // Restaurar sessão original rapidamente
          if (currentSession.session) {
            setTimeout(async () => {
              await supabase.auth.setSession(currentSession.session);
            }, 50);
          }
        }
      }

      // 2. Preparar dados para inserção na tabela usuarios
      const dadosInsercao = {
        uid: authData.user.id,
        nome: dadosUsuario.nome,
        email: dadosUsuario.email,
        celular: dadosUsuario.telefone || null,
        nivel_acesso: dadosUsuario.nivel_acesso,
        permissoes: dadosUsuario.permissoes,
        ativo: true,
        setor: null // Usuários do sistema não têm setor
      };

      console.log('Inserindo na tabela usuarios:', dadosInsercao);

      // 3. Criar registro na tabela usuarios usando service role se disponível
      const { data, error } = await supabase
        .from('funcionariousuario')
        .insert([dadosInsercao])
        .select()
        .single();

      if (error) {
        console.error('Erro na inserção na tabela usuarios:', error);
        
        // Se falhou na inserção, tentar limpar o usuário do auth (opcional)
        // Nota: Isso pode não funcionar devido às limitações do Supabase
        try {
          await supabase.auth.admin.deleteUser(authData.user.id);
        } catch (cleanupError) {
          console.warn('Não foi possível limpar usuário do auth:', cleanupError);
        }
        
        throw error;
      }

      console.log('Usuário criado com sucesso:', data);
      
      // Mensagem de sucesso baseada no método usado
      const successMessages = {
        'isolated_instance': 'Usuário criado com sucesso! Nenhuma interferência na sessão.',
        'admin_api': 'Usuário criado com sucesso via Admin API!',
        'signup_with_restore': 'Usuário criado com sucesso! Sessão restaurada.'
      };
      
      toast.success(successMessages[creationMethod] || 'Usuário criado com sucesso!');
      
      // Recarregar lista de usuários
      await carregarUsuarios();
      return data;
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      
      // Mensagens de erro mais específicas
      let errorMessage = 'Erro ao criar usuário';
      if (error.message?.includes('already registered')) {
        errorMessage = 'Este email já está cadastrado no sistema';
      } else if (error.message?.includes('password')) {
        errorMessage = 'A senha deve ter pelo menos 6 caracteres';
      } else if (error.message?.includes('email')) {
        errorMessage = 'Email inválido';
      } else if (error.message?.includes('permission denied')) {
        errorMessage = 'Sem permissão para criar usuários. Verifique se você é administrador.';
      } else if (error.message?.includes('policy')) {
        errorMessage = 'Erro de permissão: não é possível criar usuário com este nível de acesso';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar usuário
  const atualizarUsuario = async (id, dadosUsuario) => {
    try {
      setLoading(true);

      // Verificar permissões no frontend antes de tentar atualizar
      const { data: currentUser, error: permError } = await supabase
        .from('funcionariousuario')
        .select('id, nivel_acesso, permissoes, ativo')
        .eq('uid', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (permError || !currentUser) {
        throw new Error('Não foi possível verificar suas permissões');
      }

      // Verificar se pode editar usuários
      if (currentUser.nivel_acesso !== 'admin' || 
          !currentUser.ativo || 
          currentUser.permissoes?.['gestao-usuarios'] !== true) {
        throw new Error('Você não tem permissão para editar usuários');
      }

      // Verificar se está tentando editar outro admin (apenas o primeiro admin pode)
      const { data: targetUser } = await supabase
        .from('funcionariousuario')
        .select('nivel_acesso')
        .eq('id', id)
        .single();

      if (targetUser?.nivel_acesso === 'admin' && currentUser.id !== 1) {
        throw new Error('Apenas o administrador principal pode editar outros administradores');
      }

      console.log('Permissões verificadas - prosseguindo com atualização');

      // Preparar dados para atualização (excluir email que não deve ser alterado)
      const dadosAtualizacao = {
        nome: dadosUsuario.nome,
        celular: dadosUsuario.telefone,
        nivel_acesso: dadosUsuario.nivel_acesso,
        permissoes: dadosUsuario.permissoes,
        ativo: dadosUsuario.ativo
      };

      console.log('Atualizando usuário:', id, dadosAtualizacao);

      const { data, error } = await supabase
        .from('funcionariousuario')
        .update(dadosAtualizacao)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro detalhado do Supabase:', error);
        throw error;
      }

      toast.success('Usuário atualizado com sucesso!');
      await carregarUsuarios();
      return data;
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      
      // Mensagens de erro mais específicas
      let errorMessage = 'Erro ao atualizar usuário';
      if (error.message?.includes('permission denied')) {
        errorMessage = 'Sem permissão para atualizar este usuário';
      } else if (error.message?.includes('privilege')) {
        errorMessage = 'Não é possível alterar privilégios deste usuário';
      } else if (error.message?.includes('admin')) {
        errorMessage = 'Não é possível alterar nível para administrador';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Ativar/Desativar usuário
  const toggleStatusUsuario = async (id, ativo) => {
    try {
      const { data, error } = await supabase
        .from('funcionariousuario')
        .update({ ativo })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      toast.success(`Usuário ${ativo ? 'ativado' : 'desativado'} com sucesso!`);
      await carregarUsuarios();
      return data;
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      toast.error('Erro ao alterar status do usuário');
      throw error;
    }
  };

  // Redefinir senha (admin define nova senha) - DESABILITADO
  // Esta função requer service role key, que não está disponível no frontend
  const redefinirSenha = async (authUserId, novaSenha) => {
    try {
      // Esta funcionalidade foi desabilitada por questões de segurança
      // Use o "Reset por Email" em vez disso
      throw new Error('Funcionalidade desabilitada. Use "Reset por Email" para maior segurança.');
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      toast.error('Use "Reset por Email" para redefinir senhas com segurança');
      throw error;
    }
  };

  // Enviar email de reset de senha (usuário define nova senha)
  const enviarResetSenha = async (email) => {
    try {
      // Usar variável de ambiente para URL base, com fallback para window.location.origin
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${baseUrl}/reset-password`
      });

      if (error) throw error;

      toast.success('Email de reset de senha enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar email de reset:', error);
      toast.error('Erro ao enviar email de reset de senha');
      throw error;
    }
  };

  // === FUNÇÕES PARA COLABORADORES ===

  // Criar colaborador
  const criarColaborador = async (dadosColaborador) => {
    try {
      setLoading(true);
      console.log('Iniciando criação de colaborador:', dadosColaborador.email);

      // Verificar permissões no frontend antes de tentar criar
      const { data: currentUser, error: permError } = await supabase
        .from('funcionariousuario')
        .select('nivel_acesso, permissoes, ativo')
        .eq('uid', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (permError || !currentUser) {
        throw new Error('Não foi possível verificar suas permissões');
      }

      if (currentUser.nivel_acesso !== 'admin' || 
          !currentUser.ativo || 
          currentUser.permissoes?.['gestao-usuarios'] !== true) {
        throw new Error('Você não tem permissão para criar colaboradores');
      }

      console.log('Permissões verificadas - prosseguindo com criação');

      // 1. Tentar criar usuário com instância isolada (não afeta sessão atual)
      let authData;
      let creationMethod = 'unknown';

      try {
        const result = await createUserSeparateInstance({
          email: dadosColaborador.email,
          password: dadosColaborador.senha_temporaria
        });

        if (result.success) {
          authData = { user: result.user };
          creationMethod = result.method;
          console.log('Colaborador criado sem afetar sessão atual:', result.user.id);
        } else {
          throw new Error('Falha na criação com instância isolada');
        }
      } catch (isolatedError) {
        console.log('Instância isolada falhou, usando Admin API:', isolatedError.message);
        
        try {
          // 2. Fallback: tentar Admin API
          const { data: adminData, error: adminError } = await supabase.auth.admin.createUser({
            email: dadosColaborador.email,
            password: dadosColaborador.senha_temporaria,
            email_confirm: true,
            user_metadata: {
              created_by_admin: true,
              is_colaborador: true
            }
          });

          if (!adminError && adminData.user) {
            authData = adminData;
            creationMethod = 'admin_api';
            console.log('Colaborador criado via Admin API:', adminData.user.id);
          } else {
            throw adminError || new Error('Admin API falhou');
          }
        } catch (adminError) {
          console.log('Admin API falhou, usando signUp com restauração:', adminError.message);
          
          // 3. Último fallback: signUp normal com restauração rápida
          const { data: currentSession } = await supabase.auth.getSession();
          
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: dadosColaborador.email,
            password: dadosColaborador.senha_temporaria,
            options: {
              emailRedirectTo: undefined
            }
          });

          if (signUpError) {
            throw signUpError;
          }

          authData = signUpData;
          creationMethod = 'signup_with_restore';
          console.log('Colaborador criado via signUp:', signUpData.user.id);

          // Restaurar sessão original rapidamente
          if (currentSession.session) {
            setTimeout(async () => {
              await supabase.auth.setSession(currentSession.session);
            }, 50);
          }
        }
      }

      // 2. Preparar dados para inserção na tabela funcionariousuario
      const dadosInsercao = {
        uid: authData.user.id,
        nome: dadosColaborador.nome,
        email: dadosColaborador.email,
        celular: dadosColaborador.telefone || null,
        email_pessoal: dadosColaborador.email_pessoal || null,
        setor: dadosColaborador.setor,
        ativo: true,
        nivel_acesso: null, // Colaboradores não têm nível de acesso ao sistema web
        permissoes: null    // Colaboradores não têm permissões no sistema web
      };

      console.log('Inserindo na tabela funcionariousuario:', dadosInsercao);

      // 3. Criar registro na tabela funcionariousuario
      const { data, error } = await supabase
        .from('funcionariousuario')
        .insert([dadosInsercao])
        .select()
        .single();

      if (error) {
        console.error('Erro na inserção na tabela funcionariousuario:', error);
        
        // Se falhou na inserção, tentar limpar o usuário do auth (opcional)
        try {
          await supabase.auth.admin.deleteUser(authData.user.id);
        } catch (cleanupError) {
          console.warn('Não foi possível limpar usuário do auth:', cleanupError);
        }
        
        throw error;
      }

      console.log('Colaborador criado com sucesso:', data);
      
      // Mensagem de sucesso baseada no método usado
      const successMessages = {
        'isolated_instance': 'Colaborador criado com sucesso! Nenhuma interferência na sessão.',
        'admin_api': 'Colaborador criado com sucesso via Admin API!',
        'signup_with_restore': 'Colaborador criado com sucesso! Sessão restaurada.'
      };
      
      toast.success(successMessages[creationMethod] || 'Colaborador criado com sucesso!');
      
      // Recarregar lista de colaboradores
      await carregarColaboradores();
      return data;
    } catch (error) {
      console.error('Erro ao criar colaborador:', error);
      
      // Mensagens de erro mais específicas
      let errorMessage = 'Erro ao criar colaborador';
      if (error.message?.includes('already registered')) {
        errorMessage = 'Este email já está cadastrado no sistema';
      } else if (error.message?.includes('password')) {
        errorMessage = 'A senha deve ter pelo menos 6 caracteres';
      } else if (error.message?.includes('email')) {
        errorMessage = 'Email inválido';
      } else if (error.message?.includes('permission denied')) {
        errorMessage = 'Sem permissão para criar colaboradores. Verifique se você é administrador.';
      } else if (error.message?.includes('policy')) {
        errorMessage = 'Erro de permissão: não é possível criar colaborador';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar colaborador
  const atualizarColaborador = async (id, dadosColaborador) => {
    try {
      setLoading(true);

      const dadosAtualizacao = {
        nome: dadosColaborador.nome,
        email: dadosColaborador.email,
        celular: dadosColaborador.telefone,
        email_pessoal: dadosColaborador.email_pessoal,
        setor: dadosColaborador.setor,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('funcionariousuario')
        .update(dadosAtualizacao)
        .eq('id', id)
        .select();

      if (error) throw error;

      toast.success('Colaborador atualizado com sucesso!');
      
      // Recarregar lista de colaboradores
      await carregarColaboradores();
      
      return data[0];
    } catch (error) {
      console.error('Erro ao atualizar colaborador:', error);
      toast.error('Erro ao atualizar colaborador');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Toggle status do colaborador
  const toggleStatusColaborador = async (id, ativo) => {
    try {
      const { data, error } = await supabase
        .from('funcionariousuario')
        .update({ ativo })
        .eq('id', id)
        .select();

      if (error) throw error;

      toast.success(`Colaborador ${ativo ? 'ativado' : 'desativado'} com sucesso!`);
      
      // Atualizar estado local
      setColaboradores(prev => 
        prev.map(colaborador => 
          colaborador.id === id ? { ...colaborador, ativo } : colaborador
        )
      );
    } catch (error) {
      console.error('Erro ao alterar status do colaborador:', error);
      toast.error('Erro ao alterar status do colaborador');
      throw error;
    }
  };

  // Obter colaborador por ID
  const obterColaborador = useCallback(async (id) => {
    try {
      const { data, error } = await supabase
        .from('funcionariousuario')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao obter colaborador:', error);
      toast.error('Erro ao carregar dados do colaborador');
      throw error;
    }
  }, []);

  // Obter usuário por ID
  const obterUsuario = useCallback(async (id) => {
    try {
      const { data, error } = await supabase
        .from('funcionariousuario')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      // Usar dados da tabela usuarios
      return {
        ...data,
        auth_user: {
          email: data.email,
          created_at: data.created_at,
          last_sign_in_at: null
        }
      };
    } catch (error) {
      console.error('Erro ao obter usuário:', error);
      toast.error('Erro ao obter usuário');
      throw error;
    }
  }, []);

  // Estado para estatísticas da população
  const [estatisticasPopulacao, setEstatisticasPopulacao] = useState({
    usuariosAplicativo: 0,
    usuariosWhatsapp: 0
  });

  // Carregar estatísticas da população
  const carregarEstatisticasPopulacao = useCallback(async () => {
    try {
      // Carregar usuários do aplicativo
      const { data: usuariosApp, error: errorApp, count: countApp } = await supabase
        .from('usuario')
        .select('id', { count: 'exact', head: true });

      // Carregar usuários do WhatsApp
      const { data: usuariosWhats, error: errorWhats, count: countWhats } = await supabase
        .from('usuario_whatsapp')
        .select('id', { count: 'exact', head: true });

      console.log('Estatísticas população:', { countApp, countWhats, errorApp, errorWhats });

      setEstatisticasPopulacao({
        usuariosAplicativo: countApp || 0,
        usuariosWhatsapp: countWhats || 0
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas da população:', error);
      // Fallback: tentar sem count
      try {
        const { data: usuariosApp } = await supabase
          .from('usuario')
          .select('id');

        const { data: usuariosWhats } = await supabase
          .from('usuario_whatsapp')
          .select('id');

        setEstatisticasPopulacao({
          usuariosAplicativo: usuariosApp?.length || 0,
          usuariosWhatsapp: usuariosWhats?.length || 0
        });
      } catch (fallbackError) {
        console.error('Erro no fallback das estatísticas:', fallbackError);
      }
    }
  }, []);

  // Estatísticas dos usuários
  const obterEstatisticas = useCallback(() => {
    const total = usuarios.length;
    const ativos = usuarios.filter(u => u.ativo).length;
    const inativos = total - ativos;
    const administradores = usuarios.filter(u => u.nivel_acesso === 'admin').length;
    const operadores = usuarios.filter(u => u.nivel_acesso === 'operador').length;
    const visualizadores = usuarios.filter(u => u.nivel_acesso === 'visualizador').length;
    
    const totalColaboradores = colaboradores.length;
    const colaboradoresAtivos = colaboradores.filter(c => c.ativo).length;

    return {
      total,
      ativos,
      inativos,
      administradores,
      operadores,
      visualizadores,
      colaboradores: totalColaboradores,
      colaboradoresAtivos,
      populacao: estatisticasPopulacao.usuariosAplicativo + estatisticasPopulacao.usuariosWhatsapp
    };
  }, [usuarios, colaboradores, estatisticasPopulacao]);

  const value = {
    usuarios,
    colaboradores,
    setores,
    loading,
    filtros,
    setFiltros,
    filtrosColaboradores,
    setFiltrosColaboradores,
    carregarUsuarios,
    carregarColaboradores,
    carregarSetores,
    carregarEstatisticasPopulacao,
    criarUsuario,
    atualizarUsuario,
    toggleStatusUsuario,
    criarColaborador,
    atualizarColaborador,
    toggleStatusColaborador,
    obterColaborador,
    redefinirSenha,
    enviarResetSenha,
    obterUsuario,
    obterEstatisticas
  };

  return (
    <GestaoUsuariosContext.Provider value={value}>
      {children}
    </GestaoUsuariosContext.Provider>
  );
}